import { type FC } from 'react';
import { VideoPlayer } from 'dibs-video/exports/VideoPlayer.ts';
import { isVimeoUrl } from 'dibs-video/exports/helpers.ts';

import styles from './styles.scss';
import classNames from 'classnames';
import dibsCss from 'dibs-css';

const srcSetOptions = {
    sizes: '100vw',
    photoQuality: 60,
    srcSet: [{ width: 250 }, { width: 500 }, { width: 700 }, { width: 1000 }],
};

function getSrcSetAttr(
    path?: string,
    useSrcSet?: boolean
): { srcSet?: string; sizes: string } | null {
    if (!useSrcSet) {
        return null;
    }

    const { srcSet, photoQuality } = srcSetOptions;
    const basePath = `${path}?quality=${photoQuality}&`;
    const srcSetArr = srcSet.map(({ width }) => `${basePath}width=${width} ${width}w`);

    return {
        srcSet: srcSetArr.join(', '),
        sizes: srcSetOptions.sizes,
    };
}

type Props = {
    url?: string;
    path?: string;
    caption?: string;
    mediaType: 'PHOTO' | 'VIDEO';
    useSrcSet: boolean;
    vimeoStorefrontVideoHLSPlayerFF: boolean;
    isMobile: boolean;
};

const MediaItem: FC<Props> = ({
    mediaType,
    url,
    path,
    caption,
    useSrcSet,
    vimeoStorefrontVideoHLSPlayerFF,
    isMobile,
}) => {
    let component = null;
    let videoComponent = null;

    if (mediaType === 'PHOTO') {
        component = (
            <img className={styles.photo} src={path} {...getSrcSetAttr(path || '', useSrcSet)} />
        );
    } else if (mediaType === 'VIDEO') {
        if (vimeoStorefrontVideoHLSPlayerFF || !isVimeoUrl(url || '')) {
            videoComponent = (
                <div
                    className={classNames(
                        dibsCss.flex,
                        dibsCss.itemsCenter,
                        dibsCss.justifyCenter,
                        dibsCss.flexCol,
                        dibsCss.hFull,
                        dibsCss.wFull,
                        dibsCss.maxH500px,
                        dibsCss.aspectVideo,
                        styles.videoWrapper
                    )}
                >
                    <div className={dibsCss.minH0}>
                        <VideoPlayer forceMute={false}>
                            <VideoPlayer.Media src={url || ''} />
                            {isMobile ? (
                                <VideoPlayer.PlayButton />
                            ) : (
                                <VideoPlayer.Controls compact />
                            )}
                        </VideoPlayer>
                    </div>
                </div>
            );
        } else {
            component = (
                <iframe className={styles.video} src={url} frameBorder="0" allowFullScreen />
            );
        }
    }

    return (
        <>
            {videoComponent ? (
                videoComponent
            ) : (
                <div className={styles.mediaWrapper}>
                    <div className={styles.mediaContainer}>
                        {/* Media (Photo / Video) */}
                        {component}
                    </div>
                </div>
            )}
            {/* Caption */}
            {caption ? (
                <p className={styles.caption} data-tn="media-item-caption">
                    {caption}
                </p>
            ) : null}
        </>
    );
};

export default MediaItem;
